import {
    Routes,
    Route,
} from "react-router-dom";
import { Suspense } from "react";
import NavBar from '../Components/NavBar/NavBar';
import { ShopProvider } from '../Providers/ShopProvider';
import { OrderProvider } from '../Providers/OrderProvider';
import VersionControl from '../Components/VersionControl';
import { WarehouseProvider } from '../Providers/WarehouseProvider';
import MainThemeStylePicker from '../Components/MainThemeStylePicker';
import NavBarMobile from "../Components/MobileComponents/NavBarMobile.jsx/NavBarMobile";
import TopBarMobile from "../Components/MobileComponents/TopBarMobile/TopBarMobile";
import { useParams } from "../Providers/ParamsProvider";
import { Licenses } from "../Data/Licenses.ts";
import MaintenanceGuard from "../Components/MaintenanceGuard/MaintenanceGuard";
import AgreementsControl from "../Components/AgreementsControl";
import CookieModal from "@ferrodo-computer/cookie-modal"
import RequireAuth from "../Components/RequireAuth"
import { Permission } from "../Data/PermissionsEnum.ts"
import { UserRole } from "../Data/UserRolesEnum.ts"
import LoadingSpinner from "../Components/Components/LoadingSpinner";
import lazyWithReload from "../Helpers/lazyWithReload";

// Strony ładowane leniwie - każda trafia do osobnego chunka pobieranego
// dopiero przy pierwszym wejściu na trasę (code splitting).
const LoginPageIndex = lazyWithReload(() => import('../Pages/Login/LoginPageIndex'));
const ShopPageIndex = lazyWithReload(() => import('../Pages/Shop/ShopPageIndex'));
const OrdersPageIndex = lazyWithReload(() => import('../Pages/Orders/OrdersPageIndex'));
const CartPageIndex = lazyWithReload(() => import('../Pages/Cart/CartPageIndex'));
const SingleProductPageIndex = lazyWithReload(() => import('../Pages/SingleProduct/SingleProductPageIndex'));
const MyAccountIndex = lazyWithReload(() => import('../Pages/MyAccount/MyAccountIndex'));
const HomePageIndex = lazyWithReload(() => import('../Pages/Home/HomePageIndex'));
const RegistrationIndex = lazyWithReload(() => import('../Pages/Registration/RegistrationIndex'));
const SaveBaskets = lazyWithReload(() => import('../Pages/SavedBaskets/SaveBaskets'));
const FinancesPageIndex = lazyWithReload(() => import('../Pages/Finances/FinancesPageIndex'));
const OffersPageIndex = lazyWithReload(() => import('../Pages/Offers/OffersPageIndex'));
const HomePageAfterSelectCategory = lazyWithReload(() => import('../Pages/Home/Components/HomePageAfterSelectCategory'));
const OfferRequest = lazyWithReload(() => import('../Pages/Offers/OfferRequest'));
const OrderIndex = lazyWithReload(() => import('../Pages/Order/OrderIndex'));
const ConfirmAccountIndex = lazyWithReload(() => import('../Pages/ConfirmAccount/ConfirmAccountIndex'));
const RegistrationConfirmEmail = lazyWithReload(() => import('../Pages/Registration/RegistrationConfirmEmail'));
const ResetPassword = lazyWithReload(() => import('../Pages/ResetPassword/ResetPassword'));
const ChangePassword = lazyWithReload(() => import('../Pages/ResetPassword/ChangePassword'));
const ContractorsPageIndex = lazyWithReload(() => import('../Pages/Contractors/ContractorsPageIndex'));
const AboutCompanyIndex = lazyWithReload(() => import('../Components/AboutCompanyIndex'));
const OfferDetails = lazyWithReload(() => import('../Pages/Offers/OfferDetails'));
const CrmEventsPageIndex = lazyWithReload(() => import('../Pages/CrmEvents/CrmEventsPageIndex'));
const CommercialOfferWithSampleIndexesPageIndex = lazyWithReload(() => import('../Pages/CommercialOfferWithSampleIndexes/CommercialOfferWithSampleIndexesPageIndex'));
const AdminPanelPageIndex = lazyWithReload(() => import('../Pages/AdminPanel/AdminPanelPageIndex'));

const RoutesManagment = () => {
    const { isMobile, license } = useParams()

    const cookiePolicyLinks = (license == Licenses.TgInstalacje || license == Licenses.Ferrodo) ?
        [
            { href: 'https://tginstalacje.pl/polityka-prywatnosci/', label: 'Polityka prywatności' },
            { href: 'https://tginstalacje.pl/polityka-cookies/', label: 'Polityka cookies' },
        ] : []

    return (
        <ShopProvider>
            <OrderProvider>
                <WarehouseProvider>
                    <MainThemeStylePicker />
                    <MaintenanceGuard>
                        <AgreementsControl>
                            <VersionControl />
                            <CookieModal policyLinks={cookiePolicyLinks} />
                            {isMobile ? <NavBarMobile /> : <NavBar />}
                            {isMobile && <TopBarMobile />}
                            <Suspense fallback={<LoadingSpinner />}>
                                <Routes>
                                    <Route path='ConfirmAccount' element={<ConfirmAccountIndex />} />
                                    <Route path='AccountCreated' element={<RegistrationConfirmEmail />} />
                                    <Route path='ResetPassword' element={<ResetPassword />} />
                                    <Route path='ChangePassword' element={<ChangePassword />} />

                                    <Route path='/' element={<HomePageIndex />} />
                                    <Route path='login' element={<LoginPageIndex />} />
                                    <Route path='Registration' element={<RegistrationIndex />} />
                                    <Route path='CategoryDetails' element={<HomePageAfterSelectCategory />} />
                                    <Route path='Shop' element={<ShopPageIndex />} />
                                    <Route path='Finances' element={
                                        <RequireAuth roles={[UserRole.Finance]}>
                                            <FinancesPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path='Offers' element={
                                        <RequireAuth roles={[UserRole.Offers]}>
                                            <OffersPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path='OfferRequest/:mode/:forceLoad/:requestOfferNo' element={
                                        <RequireAuth roles={[UserRole.Offers]}>
                                            <OfferRequest />
                                        </RequireAuth>
                                    } />
                                    <Route path='Contractors' element={
                                        <RequireAuth permissions={[Permission.Operator]}>
                                            <ContractorsPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path='Orders' element={
                                        <RequireAuth roles={[UserRole.Orders]}>
                                            <OrdersPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path="Cart" element={<CartPageIndex />} />
                                    <Route path='MyAccount' element={
                                        <RequireAuth>
                                            <MyAccountIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path="product/:index" element={<SingleProductPageIndex />} />
                                    <Route path='SavedBaskets' element={<SaveBaskets />} />
                                    <Route path='Order/:orderNumber' element={<OrderIndex />} />
                                    <Route path="About" element={<AboutCompanyIndex />} />
                                    <Route path='OfferDetails/:offerNo/:offerSiteId/:isOfferRequest' element={<OfferDetails />} />
                                    <Route path='CrmEvents' element={
                                        <RequireAuth permissions={[Permission.Operator, { permission: Permission.CrmContacts, minLevel: 1 }]}>
                                            <CrmEventsPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path='CommercialOfferWithSampleIndexes' element={
                                        <RequireAuth permissions={[Permission.Operator]}>
                                            <CommercialOfferWithSampleIndexesPageIndex />
                                        </RequireAuth>
                                    } />
                                    <Route path='AdminPanel' element={
                                        <RequireAuth permissions={[Permission.Operator, Permission.Admin]}>
                                            <AdminPanelPageIndex />
                                        </RequireAuth>
                                    } />
                                </Routes>
                            </Suspense>
                        </AgreementsControl>
                    </MaintenanceGuard>
                </WarehouseProvider>
            </OrderProvider>
        </ShopProvider>
    )
}

export default RoutesManagment